/**
 * SyncWorker Enhanced - Funções de sincronização com melhorias de confiabilidade
 *
 * Este arquivo contém versões melhoradas das funções críticas de sincronização:
 *
 * CORREÇÕES IMPLEMENTADAS:
 * - V01: lastPulledAt só é atualizado APÓS confirmação de pullChanges
 * - V02: pullChanges usa transações atômicas
 * - V03: push com confirmação e integração com SyncQueue
 * - V04: Integração com TokenManager para evitar race conditions
 *
 * COMO USAR:
 * Importe as funções deste arquivo no lugar das originais do syncWorker.js
 * para obter as melhorias de confiabilidade.
 */

import database, { AVAILABLE_DTO_MAPPINGS, fromDTO, utils } from '../database'
import { Q } from '@nozbe/watermelondb'
import { prepareMarkAsSynced } from '@nozbe/watermelondb/sync/impl/helpers'
import find from 'lodash/find'
import flattenDeep from 'lodash/flattenDeep'
import uniq from 'lodash/uniq'
import { recordEvent } from '../observability/events'
import { syncLogger } from './SyncLogger'
import { syncQueue } from './SyncQueue'
import { tokenManager } from './TokenManager'
import { checkpointManager, CheckpointPhase } from './CheckpointManager'
import { conflictResolver } from './ConflictResolver'

// ============================================================================
// CONSTANTES
// ============================================================================

// G04: Tamanho do chunk para processamento com yield
const CHUNK_SIZE = 1000

// Entidades pesadas que se beneficiam de chunking
const HEAVY_ENTITIES = ['d0s', 'dns', 'iatfs', 'lotes']

const SYNC_OBJECT_TO_TABLE = {
  usuarios: 'users',
  tipoDominios: 'domain_types',
  valorDominios: 'domain_values',
  fazendas: 'farms',
  protocolos: 'protocols',
  lotes: 'batches',
  d0s: 'd0s',
  dns: 'dns',
  iatfs: 'iatfs',
  fazendaUsuarios: 'farms__rel__users',
  touros: 'bulls_new',
  partidas: 'partida',
  retiros: 'corrals',
}

const SYNC_OBJECT_TO_TABLE_ONLY_BATCH = {
  lotes: 'batches',
  d0s: 'd0s',
  dns: 'dns',
  iatfs: 'iatfs',
}

const toDTOMapping = (name) => {
  return name === 'farms__rel__users' ? 'FARM_USERS' : name.toUpperCase()
}

// ============================================================================
// V01 & V02: PULL CHANGES COM TRANSAÇÕES ATÔMICAS
// ============================================================================

/**
 * Resultado de uma operação de pull
 */
class PullResult {
  constructor() {
    this.success = false
    this.entitiesProcessed = {}
    this.totalInserted = 0
    this.totalUpdated = 0
    this.errors = []
  }

  addEntityResult(entity, inserted, updated, success) {
    this.entitiesProcessed[entity] = { inserted, updated, success }
    if (success) {
      this.totalInserted += inserted
      this.totalUpdated += updated
    }
  }

  addError(entity, error) {
    this.errors.push({ entity, error: error.message, stack: error.stack })
  }

  isFullySuccessful() {
    return this.errors.length === 0
  }
}

/**
 * pullChangesEnhanced - Versão melhorada com transações atômicas
 *
 * CORREÇÕES:
 * - Todas as operações de uma entidade são executadas em um único batch
 * - Se uma entidade falhar, as outras ainda são processadas
 * - Retorna resultado detalhado para decisão sobre lastPulledAt
 * - Checkpoints para recuperação de falhas (T1.4)
 * - CORREÇÃO WatermelonDB: Todas as operações são executadas em uma ÚNICA
 *   database.write() para evitar warning de actions enfileiradas
 *
 * OTIMIZAÇÕES:
 * - G04: Entidades pesadas (d0s, dns, iatfs) são processadas com yield
 *        entre chunks para permitir atualização da UI
 *
 * @param {Object} syncObject - Objeto com dados do servidor
 * @param {boolean} isOnlyBatch - Se deve processar apenas entidades de lote
 * @param {Function} onProgress - Callback opcional para progresso
 * @param {boolean} forceUpdate - Se true, ignora comparação de timestamps e sempre atualiza
 * @returns {PullResult} Resultado detalhado da operação
 */
export async function pullChangesEnhanced(
  syncObject,
  isOnlyBatch = false,
  onProgress = null,
  forceUpdate = false,
) {
  const result = new PullResult()
  const startTime = Date.now()

  // ================================================================
  // CHECKPOINT T1.4: Criar checkpoint antes de aplicar mudanças
  // ================================================================
  const checkpointId = await checkpointManager.createCheckpoint(
    CheckpointPhase.PULL_RECEIVED,
    {
      isOnlyBatch,
      entityCount: Object.keys(syncObject).filter(
        (k) => Array.isArray(syncObject[k]) && syncObject[k].length > 0,
      ).length,
      timestamp: Date.now(),
    },
  )

  syncLogger.log('PULL_CHANGES_START', {
    isOnlyBatch,
    forceUpdate,
    checkpointId,
    entities: Object.keys(syncObject).filter(
      (k) => Array.isArray(syncObject[k]) && syncObject[k].length > 0,
    ),
  })

  // FIX: Log para debug de forceUpdate
  if (forceUpdate) {
  }

  const values = isOnlyBatch
    ? ['d0s', 'dns', 'iatfs', 'lotes']
    : [
        'usuarios',
        'tipoDominios',
        'valorDominios',
        'fazendas',
        'retiros',
        'protocolos',
        'lotes',
        'd0s',
        'dns',
        'iatfs',
        'fazendaUsuarios',
        'touros',
        'partidas',
      ]

  // Calcular total de registros para progresso
  let totalRecords = 0
  let processedRecords = 0
  for (const entityName of values) {
    if (syncObject[entityName] && Array.isArray(syncObject[entityName])) {
      totalRecords += syncObject[entityName].length
    }
  }

  // ================================================================
  // G04: Processar entidades com yield entre chunks para UI responsiva
  // ================================================================
  try {
    for (let i = 0; i < values.length; i++) {
      const entityName = values[i]

      // Verificar se há dados para esta entidade
      if (
        !syncObject[entityName] ||
        !Array.isArray(syncObject[entityName]) ||
        syncObject[entityName].length === 0
      ) {
        continue
      }

      const entityData = syncObject[entityName]
      const isHeavyEntity = HEAVY_ENTITIES.includes(entityName)

      // Emitir progresso
      if (onProgress) {
        try {
          onProgress({
            entity: entityName,
            entityIndex: i,
            totalEntities: values.length,
            recordsProcessed: processedRecords,
            totalRecords,
            phase: 'processing',
          })
        } catch (e) {
          console.warn('[SYNC] Progress callback error:', e.message)
        }
      }

      try {
        // G04: Para entidades pesadas com muitos registros, processar em chunks
        // Cada chunk é uma transação separada com yield para UI
        if (isHeavyEntity && entityData.length > CHUNK_SIZE) {
          const chunks = []
          for (let j = 0; j < entityData.length; j += CHUNK_SIZE) {
            chunks.push(entityData.slice(j, j + CHUNK_SIZE))
          }

          let entityInserted = 0
          let entityUpdated = 0

          for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
            const chunk = chunks[chunkIndex]

            // Processar chunk em sua própria transação
            await database.action(async () => {
              const chunkResult = await processEntityWithinTransaction(
                entityName,
                chunk,
                isOnlyBatch,
                forceUpdate,
              )
              entityInserted += chunkResult.inserted
              entityUpdated += chunkResult.updated
            })

            processedRecords += chunk.length

            // G04: Yield para permitir atualização da UI
            await new Promise((resolve) => setTimeout(resolve, 0))

            // Emitir progresso por chunk
            if (onProgress) {
              try {
                onProgress({
                  entity: entityName,
                  entityIndex: i,
                  totalEntities: values.length,
                  recordsProcessed: processedRecords,
                  totalRecords,
                  chunkIndex: chunkIndex + 1,
                  totalChunks: chunks.length,
                  phase: 'chunk',
                })
              } catch (e) {
                console.warn('[SYNC] Progress callback error:', e.message)
              }
            }
          }

          result.addEntityResult(
            entityName,
            entityInserted,
            entityUpdated,
            true,
          )
          syncLogger.logPull(
            entityName,
            entityInserted,
            entityUpdated,
            'success',
          )
        } else {
          // Entidades leves ou com poucos registros - processar tudo de uma vez
          await database.action(async () => {
            const entityResult = await processEntityWithinTransaction(
              entityName,
              entityData,
              isOnlyBatch,
              forceUpdate,
            )

            result.addEntityResult(
              entityName,
              entityResult.inserted,
              entityResult.updated,
              true,
            )

            syncLogger.logPull(
              entityName,
              entityResult.inserted,
              entityResult.updated,
              'success',
            )

            // Processar entidades filhas (protocolos -> managementProtocols)
            if (entityName === 'protocolos') {
              await processManagementProtocolsWithinTransaction(entityData)
            }

            // Processar entidades filhas (usuarios -> roles)
            if (entityName === 'usuarios') {
              await processUserRolesWithinTransaction(entityData)
            }
          })

          processedRecords += entityData.length
        }

        // G04: Yield entre entidades para UI responsiva
        await new Promise((resolve) => setTimeout(resolve, 0))
      } catch (error) {
        result.addError(entityName, error)
        syncLogger.error('PULL_ENTITY_FAILED', error, { entity: entityName })
      }
    }
  } catch (writeError) {
    syncLogger.error('PULL_WRITE_FAILED', writeError)
    result.addError('_transaction', writeError)
  }

  result.success = result.isFullySuccessful()

  // ================================================================
  // CHECKPOINT T1.4: Completar checkpoint após sucesso
  // ================================================================
  if (result.success && checkpointId) {
    await checkpointManager.completeCheckpoint(checkpointId)
  } else if (checkpointId) {
    // Se houve erros, atualizar checkpoint com informações de falha
    await checkpointManager.updateCheckpoint(checkpointId, {
      phase: CheckpointPhase.PULL_APPLYING,
      errors: result.errors,
      partialSuccess: result.totalInserted > 0 || result.totalUpdated > 0,
    })
  }

  syncLogger.log('PULL_CHANGES_COMPLETE', {
    success: result.success,
    duration: Date.now() - startTime,
    totalInserted: result.totalInserted,
    totalUpdated: result.totalUpdated,
    errors: result.errors.length,
    checkpointId,
  })

  return result
}

/**
 * Processa uma entidade em uma única transação atômica
 * NOTA: Esta função cria sua própria database.action() - usar apenas quando
 * NÃO estiver dentro de outra transação
 */
async function processEntityWithTransaction(
  entityName,
  serverData,
  isOnlyBatch,
) {
  let result
  await database.action(async () => {
    result = await processEntityWithinTransaction(
      entityName,
      serverData,
      isOnlyBatch,
    )
  })
  return result
}

/**
 * Processa uma entidade DENTRO de uma transação já existente
 * Esta versão NÃO cria sua própria database.action() - deve ser chamada
 * de dentro de um database.write() ou database.action()
 *
 * OTIMIZAÇÕES APLICADAS:
 * - G01: Map/Set para acesso O(1) em vez de find() O(n)
 * - G02: Pré-cálculo de timestamps evita conversão repetida
 * - G03: REMOVIDO prepareMarkAsSynced desnecessário
 *
 * @param {boolean} forceUpdate - Se true, sempre atualiza registros do servidor (ignora comparação de timestamps)
 */
async function processEntityWithinTransaction(
  entityName,
  serverData,
  isOnlyBatch,
  forceUpdate = false,
) {
  const entityStartTime = Date.now()

  const tableName = isOnlyBatch
    ? SYNC_OBJECT_TO_TABLE_ONLY_BATCH[entityName]
    : SYNC_OBJECT_TO_TABLE[entityName]

  const mappingName = toDTOMapping(tableName)

  let inserted = 0
  let updated = 0

  const collection = database.collections.get(tableName)

  // OTIMIZAÇÃO G01: Criar Map de dados do servidor para acesso O(1)
  // BUG FIX: Filtrar items sem ID válido para evitar perda silenciosa de dados
  const validItems = serverData.filter((item) => item.id != null)
  const serverDataMap = new Map(validItems.map((item) => [item.id, item]))

  if (validItems.length < serverData.length) {
    recordEvent({
      level: 'warn',
      category: 'sync',
      action: 'items_without_id_ignored',
      payload: {
        entity: entityName,
        ignoredCount: serverData.length - validItems.length,
      },
    })
  }

  // OTIMIZAÇÃO G02: Pré-calcular todos os timestamps do servidor
  // BUG FIX: new Date() não lança exceção para valores inválidos,
  // apenas retorna NaN. Verificar explicitamente.
  const serverTimestamps = new Map()
  for (const [id, item] of serverDataMap) {
    const timestamp = new Date(item.updatedAt).getTime()
    serverTimestamps.set(id, isNaN(timestamp) ? 0 : timestamp)
  }

  // 1. Buscar registros existentes
  const serverIds = serverData.map((item) => item.id)
  const existingEntries = await collection
    .query(Q.where('id', Q.oneOf(serverIds)))
    .fetch()

  // OTIMIZAÇÃO G01: Map de registros existentes para acesso O(1)
  const existingMap = new Map(existingEntries.map((e) => [e.id, e]))

  // 2. Separar em inserções e atualizações
  const toInsert = []
  const toUpdate = []

  for (const serverItem of serverData) {
    const existing = existingMap.get(serverItem.id)

    if (!existing) {
      // Novo registro - inserir
      toInsert.push(serverItem)
    } else {
      // OTIMIZAÇÃO G02: Usar timestamps pré-calculados
      const serverTimestamp = serverTimestamps.get(serverItem.id)
      const localTimestamp = new Date(existing._raw.updated_at).getTime()

      // FIX: Quando forceUpdate=true, sempre atualizar (>= em vez de >)
      // Isso garante que dados do servidor sejam aplicados quando o usuário
      // explicitamente clica em SINCRONIZAR
      const shouldUpdate = forceUpdate
        ? serverTimestamp >= localTimestamp
        : serverTimestamp > localTimestamp

      if (shouldUpdate) {
        // Servidor é mais recente (ou forceUpdate está ativo) - verificar conflito
        // T4.1: Usar ConflictResolver para detecção 3-way
        if (existing._raw._status !== 'synced') {
          const conflict = conflictResolver.detectConflict(
            tableName,
            existing,
            serverItem,
            null, // base data seria útil se tivéssemos histórico
          )

          if (conflict) {
            // Resolver conflito e obter dados finais
            const resolvedData = conflictResolver.resolveConflict(conflict)
            toUpdate.push({
              existing,
              serverItem: resolvedData,
              hadConflict: true,
            })
          } else {
            // Sem conflito real, atualizar normalmente
            toUpdate.push({ existing, serverItem, hadConflict: false })
          }
        } else {
          // Local synced, atualizar sem conflito
          toUpdate.push({ existing, serverItem, hadConflict: false })
        }
      }
    }
  }

  // 3. Preparar todas as operações
  const batchOperations = []

  // Preparar updates
  // FIX: Forçar _status='synced' após prepareUpdate para evitar reenvio
  // O WatermelonDB automaticamente muda _status para 'updated' em prepareUpdate()
  for (const { existing, serverItem } of toUpdate) {
    batchOperations.push(
      existing.prepareUpdate((record) => {
        fromDTO(AVAILABLE_DTO_MAPPINGS[mappingName], serverItem, record, true)
        // CRÍTICO: Forçar status como synced para evitar reenvio
        record._raw._status = 'synced'
        record._raw._changed = ''
      }),
    )
  }

  // Preparar inserts
  for (const serverItem of toInsert) {
    batchOperations.push(
      collection.prepareCreate((record) => {
        record._raw.id = serverItem.id
        record._raw._status = 'synced'
        record._raw._changed = ''
        fromDTO(AVAILABLE_DTO_MAPPINGS[mappingName], serverItem, record, true)
      }),
    )
  }

  // OTIMIZAÇÃO G03: REMOVIDO prepareMarkAsSynced desnecessário
  // Registros já synced não precisam ser remarcados - isso era redundante

  // 4. Executar TUDO em um único batch atômico
  if (batchOperations.length > 0) {
    await database.batch(...batchOperations)
  }

  inserted = toInsert.length
  updated = toUpdate.length

  const entityDuration = Date.now() - entityStartTime
  if (entityDuration > 1000) {
    recordEvent({
      level: 'warn',
      category: 'sync',
      action: 'slow_entity_pull_enhanced',
      payload: {
        entity: entityName,
        durationMs: entityDuration,
        recordsCount: serverData.length,
        updatesCount: updated,
        insertsCount: inserted,
      },
    })
  }

  return { inserted, updated }
}

/**
 * Processa ManagementProtocols (filhos de protocolos)
 * NOTA: Esta função cria sua própria database.action()
 */
async function processManagementProtocols(protocolsData) {
  await database.action(async () => {
    await processManagementProtocolsWithinTransaction(protocolsData)
  })
}

/**
 * Processa ManagementProtocols DENTRO de uma transação já existente
 * Esta versão NÃO cria sua própria database.action()
 *
 * OTIMIZAÇÕES APLICADAS:
 * - G01: Map para acesso O(1)
 * - G02: Pré-cálculo de timestamps
 * - G03: REMOVIDO prepareMarkAsSynced desnecessário
 */
async function processManagementProtocolsWithinTransaction(protocolsData) {
  const managementProtocolsData = flattenDeep(
    protocolsData.map((protocol) => {
      return (protocol.managementProtocols || []).map((mp) => ({
        ...mp,
        managementProtocolId: mp.managementProtocolId || protocol.id,
      }))
    }),
  )

  if (managementProtocolsData.length === 0) return

  const collection = database.collections.get('management_protocols')

  // OTIMIZAÇÃO G01: Criar Map para acesso O(1)
  // BUG FIX: Filtrar items sem ID válido
  const validMpItems = managementProtocolsData.filter((item) => item.id != null)
  const serverDataMap = new Map(validMpItems.map((item) => [item.id, item]))

  if (validMpItems.length < managementProtocolsData.length) {
    recordEvent({
      level: 'warn',
      category: 'sync',
      action: 'items_without_id_ignored',
      payload: {
        entity: 'managementProtocols',
        ignoredCount: managementProtocolsData.length - validMpItems.length,
        sampleParentIds: managementProtocolsData
          .filter((item) => item.id == null)
          .slice(0, 5)
          .map((item) => item.managementProtocolId),
      },
    })
  }

  // OTIMIZAÇÃO G02: Pré-calcular timestamps
  // BUG FIX: Verificar NaN explicitamente
  const serverTimestamps = new Map()
  for (const [id, item] of serverDataMap) {
    const timestamp = new Date(item.updatedAt).getTime()
    serverTimestamps.set(id, isNaN(timestamp) ? 0 : timestamp)
  }

  const existingEntries = await collection
    .query(Q.where('id', Q.oneOf(managementProtocolsData.map((e) => e.id))))
    .fetch()

  const existingMap = new Map(existingEntries.map((e) => [e.id, e]))
  const batchOperations = []

  for (const serverItem of managementProtocolsData) {
    const existing = existingMap.get(serverItem.id)

    if (!existing) {
      batchOperations.push(
        collection.prepareCreate((record) => {
          record._raw.id = serverItem.id
          record._raw._status = 'synced'
          fromDTO(
            AVAILABLE_DTO_MAPPINGS.MANAGEMENT_PROTOCOLS,
            serverItem,
            record,
            true,
          )
        }),
      )
    } else {
      // OTIMIZAÇÃO G02: Usar timestamps pré-calculados
      const serverTimestamp = serverTimestamps.get(serverItem.id)
      const localTimestamp = new Date(existing._raw.updated_at).getTime()

      // FIX: Quando forceUpdate=true, sempre atualizar (>= em vez de >)
      const shouldUpdateMp = forceUpdate
        ? serverTimestamp >= localTimestamp
        : serverTimestamp > localTimestamp

      if (shouldUpdateMp) {
        batchOperations.push(
          // FIX: Forçar _status='synced' após prepareUpdate para evitar reenvio
          existing.prepareUpdate((record) => {
            fromDTO(
              AVAILABLE_DTO_MAPPINGS.MANAGEMENT_PROTOCOLS,
              serverItem,
              record,
              true,
            )
            // CRÍTICO: Forçar status como synced
            record._raw._status = 'synced'
            record._raw._changed = ''
          }),
        )
      }
    }
  }

  // OTIMIZAÇÃO G03: REMOVIDO prepareMarkAsSynced desnecessário

  if (batchOperations.length > 0) {
    await database.batch(...batchOperations)
  }
}

/**
 * Processa Roles (filhos de usuarios)
 * NOTA: Esta função cria sua própria database.action()
 */
async function processUserRoles(usersData) {
  await database.action(async () => {
    await processUserRolesWithinTransaction(usersData)
  })
}

/**
 * Processa Roles DENTRO de uma transação já existente
 * Esta versão NÃO cria sua própria database.action()
 *
 * OTIMIZAÇÕES APLICADAS:
 * - G01: Map/Set para acesso O(1)
 * - G03: REMOVIDO prepareMarkAsSynced desnecessário
 */
async function processUserRolesWithinTransaction(usersData) {
  const rolesData = flattenDeep(
    usersData.map((user) =>
      (user.roles || []).map((role) => ({
        backend_id: role.id,
        name: role.name,
        description: role.description,
        userId: user.id,
      })),
    ),
  )

  if (rolesData.length === 0) return

  const collection = database.collections.get('roles')

  // OTIMIZAÇÃO G01: Criar Map com chave composta para acesso O(1)
  const rolesMap = new Map(
    rolesData.map((role) => [`${role.backend_id}_${role.userId}`, role]),
  )

  const userIds = uniq(rolesData.map((r) => r.userId))
  const existingEntries = await collection
    .query(Q.where('user_id', Q.oneOf(userIds)))
    .fetch()

  // OTIMIZAÇÃO G01: Criar Set de chaves existentes para lookup O(1)
  const existingKeys = new Set(
    existingEntries.map((e) => `${e.backend_id}_${e._raw.user_id}`),
  )

  const batchOperations = []

  // OTIMIZAÇÃO G01: Filtrar usando Set.has() em vez de .find()
  const rolesToInsert = rolesData.filter(
    (role) => !existingKeys.has(`${role.backend_id}_${role.userId}`),
  )

  // OTIMIZAÇÃO G01: Separar updates e deletes usando Map O(1)
  const rolesToUpdate = []
  const rolesToDelete = []

  for (const entry of existingEntries) {
    const key = `${entry.backend_id}_${entry._raw.user_id}`
    if (rolesMap.has(key)) {
      rolesToUpdate.push(entry)
    } else {
      rolesToDelete.push(entry)
    }
  }

  // Inserts
  for (const role of rolesToInsert) {
    batchOperations.push(
      collection.prepareCreate((record) => {
        record._raw._status = 'synced'
        fromDTO(AVAILABLE_DTO_MAPPINGS.ROLES, role, record, true)
      }),
    )
  }

  // Updates - usando Map O(1)
  // FIX: Forçar _status='synced' após prepareUpdate para evitar reenvio
  for (const entry of rolesToUpdate) {
    const key = `${entry.backend_id}_${entry._raw.user_id}`
    const roleData = rolesMap.get(key)
    batchOperations.push(
      entry.prepareUpdate((record) => {
        fromDTO(AVAILABLE_DTO_MAPPINGS.ROLES, roleData, record, true)
        // CRÍTICO: Forçar status como synced
        record._raw._status = 'synced'
        record._raw._changed = ''
      }),
    )
  }

  // Deletes
  for (const entry of rolesToDelete) {
    batchOperations.push(
      entry.prepareUpdate((record) => {
        record.isDeleted = true
      }),
    )
  }

  // OTIMIZAÇÃO G03: REMOVIDO prepareMarkAsSynced desnecessário

  if (batchOperations.length > 0) {
    await database.batch(...batchOperations)
  }
}

// ============================================================================
// V01: ATUALIZAÇÃO SEGURA DE lastPulledAt
// ============================================================================

/**
 * Atualiza lastPulledAt apenas se pullChanges foi bem-sucedido
 *
 * @param {string} userId - ID do usuário
 * @param {string} newLastPulledAt - Novo valor de lastPulledAt
 * @param {PullResult} pullResult - Resultado do pullChanges
 * @returns {boolean} Se a atualização foi feita
 */
export async function updateLastPulledAtSafely(
  userId,
  newLastPulledAt,
  pullResult,
) {
  if (!pullResult.success) {
    syncLogger.log('LAST_PULLED_AT_SKIPPED', {
      reason: 'pull_had_errors',
      errors: pullResult.errors,
    })
    return false
  }

  try {
    await database.adapter.setLocal(`${userId}_lastPulledAt`, newLastPulledAt)
    localStorage.setItem('lastSyncPull', new Date().toISOString())

    syncLogger.log('LAST_PULLED_AT_UPDATED', {
      userId,
      newValue: newLastPulledAt,
    })

    return true
  } catch (error) {
    syncLogger.error('LAST_PULLED_AT_FAILED', error, { userId })
    return false
  }
}

/**
 * Atualiza lastPulledAt para fazendas específicas
 */
export async function updateFarmLastPulledAtSafely(
  farmIds,
  newLastPulledAt,
  pullResult,
) {
  if (!pullResult.success) {
    syncLogger.log('FARM_LAST_PULLED_AT_SKIPPED', {
      reason: 'pull_had_errors',
      farmIds,
    })
    return false
  }

  try {
    await Promise.all(
      farmIds.map((farmId) =>
        database.adapter.setLocal(`${farmId}_lastPulledAt`, newLastPulledAt),
      ),
    )

    syncLogger.log('FARM_LAST_PULLED_AT_UPDATED', {
      farmIds,
      newValue: newLastPulledAt,
    })

    return true
  } catch (error) {
    syncLogger.error('FARM_LAST_PULLED_AT_FAILED', error, { farmIds })
    return false
  }
}

// ============================================================================
// V03: PUSH COM CONFIRMAÇÃO E QUEUE
// ============================================================================

/**
 * Resultado de uma operação de push
 */
export class PushResult {
  constructor() {
    this.success = true
    this.entitiesSent = {}
    this.totalSent = 0
    this.totalFailed = 0
    this.queuedForRetry = 0
    this.errors = []
  }

  addSuccess(entityType, count) {
    this.entitiesSent[entityType] = { count, status: 'sent' }
    this.totalSent += count
  }

  addFailure(entityType, count, error) {
    this.entitiesSent[entityType] = { count, status: 'failed', error }
    this.totalFailed += count
    this.errors.push({ entityType, error })
    this.success = false
  }

  addQueued(entityType, count) {
    this.entitiesSent[entityType] = { count, status: 'queued' }
    this.queuedForRetry += count
  }
}

/**
 * pushEnhanced - Versão melhorada do push com garantia de entrega.
 *
 * ⚠️ CAMINHO DE PRODUÇÃO. `USE_ENHANCED_SYNC = true` em syncWorker.js:50
 * faz com que esta seja a função efetivamente executada. A `push()` legacy
 * em syncWorker.js está marcada para remoção.
 *
 * REGRA: features de push novas vão em UMA dessas duas localizações:
 *   1. Aqui (pushEnhanced) se forem específicas do fluxo de push, OU
 *   2. Em `sync()` (syncWorker.js:~383) após a bifurcação, se a feature
 *      é independente do path escolhido (ex.: pushEvents de observabilidade).
 *
 * NÃO adicione só na `push()` legacy — o código fica órfão em prod.
 *
 * MELHORIAS:
 * - Integração com SyncQueue para operações que falham
 * - Logging estruturado de cada etapa
 * - Não marca como synced até confirmação real
 * - Processa fila de operações pendentes
 *
 * @param {string} token - Token de autenticação
 * @param {Function} sendDataFn - Função que envia dados para o servidor
 * @param {Function} getLoggedUserFn - Função para obter usuário logado
 * @param {boolean} isOnlyPull - Se true, não envia dados (apenas simula)
 * @returns {PushResult} Resultado detalhado do push
 */
export async function pushEnhanced(
  token,
  sendDataFn,
  getLoggedUserFn,
  isOnlyPull = false,
) {
  const result = new PushResult()
  const startTime = Date.now()

  // ================================================================
  // CHECKPOINT T1.4: Criar checkpoint antes do push
  // ================================================================
  const checkpointId = !isOnlyPull
    ? await checkpointManager.createCheckpoint(CheckpointPhase.PUSH_STARTED, {
        timestamp: Date.now(),
        isOnlyPull,
      })
    : null

  syncLogger.log('PUSH_START', { isOnlyPull, checkpointId })

  // 1. Primeiro, processar fila de operações pendentes de ciclos anteriores
  const queueStats = await syncQueue.getStats()
  if (queueStats.pending > 0) {
    syncLogger.log('PUSH_PROCESSING_PENDING_QUEUE', {
      pendingCount: queueStats.pending,
    })

    try {
      const queueResult = await processPushQueue(sendDataFn, token)
      syncLogger.log('PUSH_QUEUE_PROCESSED', queueResult)
    } catch (queueError) {
      syncLogger.error('PUSH_QUEUE_ERROR', queueError)
    }
  }

  // 2. Se é apenas pull, retornar sucesso
  if (isOnlyPull) {
    syncLogger.log('PUSH_SKIP_ONLY_PULL', {})
    return result
  }

  // Obter userId
  const loggedUserInfo = await getLoggedUserFn()
  const loggedUser = loggedUserInfo?.userId

  if (!loggedUser) {
    syncLogger.log('PUSH_SKIP_NO_USER', {})
    return result
  }

  // 3. Processar cada tipo de entidade
  const entityTypes = [
    'fazendas',
    'retiros',
    'protocolos',
    'lotes',
    'd0s',
    'dns',
    'iatfs',
    'fazendaUsuarios',
    'touros',
    'partidas',
  ]

  for (const entityType of entityTypes) {
    try {
      const entityResult = await pushEntityEnhanced(
        entityType,
        token,
        loggedUser,
        sendDataFn,
      )

      if (entityResult.sent) {
        result.addSuccess(entityType, entityResult.count)
      } else if (entityResult.queued) {
        result.addQueued(entityType, entityResult.count)
      } else if (entityResult.count === 0) {
        // Nada para enviar - não é erro
      } else {
        result.addFailure(entityType, entityResult.count, entityResult.error)
      }
    } catch (error) {
      syncLogger.error('PUSH_ENTITY_ERROR', error, { entityType })
      result.addFailure(entityType, 0, error.message)
    }
  }

  // ================================================================
  // CHECKPOINT T1.4: Completar checkpoint após push
  // ================================================================
  if (checkpointId) {
    if (result.success) {
      await checkpointManager.completeCheckpoint(checkpointId)
    } else {
      await checkpointManager.updateCheckpoint(checkpointId, {
        phase: CheckpointPhase.PUSH_ENTITY_SENT,
        errors: result.errors,
        partialSuccess: result.totalSent > 0,
        entitiesSent: result.entitiesSent,
      })
    }
  }

  syncLogger.log('PUSH_COMPLETE', {
    duration: Date.now() - startTime,
    totalSent: result.totalSent,
    totalFailed: result.totalFailed,
    queuedForRetry: result.queuedForRetry,
    success: result.success,
    checkpointId,
  })

  return result
}

/**
 * Processa push de uma entidade específica
 */
async function pushEntityEnhanced(entityType, token, loggedUser, sendDataFn) {
  const tableName = SYNC_OBJECT_TO_TABLE[entityType]

  let entityResult = {
    count: 0,
    sent: false,
    queued: false,
    error: null,
  }

  await database.action(async () => {
    const collection = database.collections.get(tableName)

    // Buscar tipo da entidade
    const entityEntry = await database.collections
      .get('domain_values')
      .query(
        Q.on('domain_types', 'nome', 'entidade'),
        Q.where('valor', normalizeEntityNameInternal(entityType)),
      )
      .fetch()

    if (!entityEntry || entityEntry.length === 0) {
      return
    }

    // Buscar registros não sincronizados
    const notSyncedQuery = Q.where('_status', Q.notEq('synced'))
    const toSync = await collection.query(notSyncedQuery).fetch()

    if (toSync.length === 0) {
      return
    }

    entityResult.count = toSync.length

    // Determinar IDs de registros atualizados
    const updatedIds = toSync
      .filter((x) => x.syncStatus === 'updated')
      .map((x) => x.id)

    // Criar payload
    const contentPayload = await Promise.all(
      toSync.map(async (entry) => {
        const verb =
          updatedIds.includes(entry.id) && !entry.priority
            ? 'PUT'
            : updatedIds.includes(entry.id) && entry.priority
              ? 'PRIORITY'
              : 'POST'

        return createPayloadForEnhanced(entry, entityType, verb)
      }),
    )

    const payload = {
      tipoId: entityEntry[0].id,
      loggedUser,
      contents: contentPayload,
    }

    // Tentar enviar
    syncLogger.logPush(entityType, toSync.length, 'sending')

    try {
      const sent = await sendDataFn(payload, token)

      if (sent) {
        // Sucesso! Marcar como synced
        entityResult.sent = true

        // Verificar se entradas não foram modificadas durante o envio
        const entriesToMark = await filterUnmodifiedEntries(collection, toSync)

        if (entriesToMark.length > 0) {
          await database.batch(
            ...entriesToMark.map((entry) => prepareMarkAsSynced(entry)),
          )
        }

        syncLogger.logPush(entityType, entriesToMark.length, 'success')
      } else {
        // Falha no envio - enfileirar para retry
        entityResult.queued = true
        entityResult.error = 'Send failed'

        await enqueueForSyncInternal(payload, toSync, entityType)
        syncLogger.logPush(entityType, toSync.length, 'queued')
      }
    } catch (sendError) {
      // Erro no envio - enfileirar para retry
      entityResult.queued = true
      entityResult.error = sendError.message

      await enqueueForSyncInternal(payload, toSync, entityType)
      syncLogger.error('PUSH_SEND_ERROR', sendError, { entityType })
    }
  })

  return entityResult
}

/**
 * Filtra entradas que não foram modificadas desde o início do push
 */
async function filterUnmodifiedEntries(collection, originalEntries) {
  const filtered = []

  for (const entry of originalEntries) {
    try {
      const currentEntry = await collection.find(entry.id)
      const originalUpdatedAt = entry._raw.updated_at
      const currentUpdatedAt = currentEntry._raw.updated_at

      // Só marcar se não foi modificado durante o envio
      if (originalUpdatedAt >= currentUpdatedAt) {
        filtered.push(currentEntry)
      }
    } catch {
      // Entry pode ter sido deletado - ignorar
    }
  }

  return filtered
}

/**
 * Cria payload para uma entrada
 * CORREÇÃO: Agora processa mapping.children (ex: managementProtocols de protocolos)
 *
 * @param {Object} entry - Registro do WatermelonDB
 * @param {string} entityType - Tipo da entidade (ex: 'protocolos')
 * @param {string} verb - Verbo HTTP (POST/PUT)
 * @param {boolean} firstPass - Se é a primeira chamada (para estrutura do payload)
 * @param {string} mappingType - Tipo de mapping direto (para chamadas recursivas)
 */
async function createPayloadForEnhanced(
  entry,
  entityType,
  verb,
  firstPass = true,
  mappingType = null,
) {
  // Usar o toDTO do database
  const { toDTO, AVAILABLE_DTO_MAPPINGS } = await import('../database')

  // Se é firstPass, usa entityType para determinar o mapping
  // Senão, usa mappingType diretamente (para children)
  const mappingName = firstPass
    ? toDTOMapping(SYNC_OBJECT_TO_TABLE[entityType])
    : mappingType

  const mapping = AVAILABLE_DTO_MAPPINGS[mappingName]
  const json = toDTO(mapping, entry)

  // CORREÇÃO: Processar children (ex: managementProtocols em protocolos)
  if (mapping.children) {
    for (const child of mapping.children) {
      // Buscar registros filhos via relação WatermelonDB @children
      const childRecords = await entry[child.name].fetch()

      // Filtrar deletados e converter cada um para DTO recursivamente
      json[child.name] = await Promise.all(
        childRecords
          .filter((element) => !element.isDeleted)
          .map(
            async (element) =>
              await createPayloadForEnhanced(
                element,
                null,
                verb,
                false,
                child.type,
              ),
          ),
      )
    }
  }

  // Processar campos renamed com datas (como na função original)
  if (mapping.renamed) {
    const { utils } = await import('../database')
    for (const field of mapping.renamed) {
      if (field.isDate && json[field.dtoName]) {
        json[field.dtoName] = utils.fromMoment(json[field.dtoName])
      }
    }
  }

  if (firstPass) {
    return {
      verbo: verb,
      json: {
        ...json,
        createdAt: json.createdAt?.valueOf?.() || json.createdAt,
        updatedAt: json.updatedAt?.valueOf?.() || json.updatedAt,
      },
    }
  } else {
    // Para children, retornar objeto simples sem wrapper de verbo
    return {
      ...json,
      createdAt: json.createdAt?.valueOf?.() || json.createdAt,
      updatedAt: json.updatedAt?.valueOf?.() || json.updatedAt,
    }
  }
}

/**
 * Normaliza nome da entidade
 */
function normalizeEntityNameInternal(name) {
  if (name === 'fazendaUsuarios') return 'fazenda_usuario'
  return name.substring(0, name.length - 1)
}

/**
 * Enfileira para sync (versão interna)
 */
async function enqueueForSyncInternal(payload, entries, entityType) {
  const operation = {
    entityType,
    payload,
    entryIds: entries.map((e) => e.id),
    createdAt: Date.now(),
  }

  await syncQueue.enqueue(operation)
}

/**
 * Enfileira dados para envio com garantia de entrega (exportada)
 *
 * @param {Object} payload - Dados para enviar
 * @param {Array} entries - Entidades originais (para marcar como synced depois)
 * @param {string} entityType - Tipo da entidade
 */
export async function enqueueForSync(payload, entries, entityType) {
  await enqueueForSyncInternal(payload, entries, entityType)

  syncLogger.log('PUSH_ENQUEUED', {
    entityType,
    count: entries.length,
  })
}

/**
 * Processa a fila de envio pendente
 *
 * @param {Function} sendDataFn - Função que envia dados para o servidor
 * @param {string} token - Token de autenticação
 */
export async function processPushQueue(sendDataFn, token) {
  const result = await syncQueue.processQueue(async (operation) => {
    syncLogger.log('PUSH_QUEUE_PROCESSING', {
      entityType: operation.entityType,
      entryCount: operation.entryIds?.length,
    })

    const sent = await sendDataFn(operation.payload, token)

    if (sent) {
      // Marcar entradas como synced
      await markEntriesAsSynced(operation.entityType, operation.entryIds)
    }

    return sent
  })

  return result
}

/**
 * Marca entradas como sincronizadas após confirmação do servidor
 */
async function markEntriesAsSynced(entityType, entryIds) {
  const tableName = SYNC_OBJECT_TO_TABLE[entityType]
  if (!tableName) return

  await database.action(async () => {
    const collection = database.collections.get(tableName)
    const entries = await collection
      .query(Q.where('id', Q.oneOf(entryIds)))
      .fetch()

    // Verificar se as entradas não foram modificadas desde o envio
    const toMark = entries.filter((entry) => {
      // Só marcar como synced se ainda está pendente
      return entry._raw._status !== 'synced'
    })

    if (toMark.length > 0) {
      await database.batch(...toMark.map((entry) => prepareMarkAsSynced(entry)))
    }

    syncLogger.log('ENTRIES_MARKED_SYNCED', {
      entityType,
      requested: entryIds.length,
      marked: toMark.length,
    })
  })
}

// ============================================================================
// V04: INTEGRAÇÃO COM TOKEN MANAGER
// ============================================================================

/**
 * Obtém token válido usando TokenManager
 *
 * @param {Function} getLoggedUserFn - Função getLoggedUser
 * @param {Function} refreshTokenFn - Função refreshToken
 */
export async function getValidToken(getLoggedUserFn, refreshTokenFn) {
  return tokenManager.getValidToken(getLoggedUserFn, refreshTokenFn)
}

/**
 * Força refresh do token
 */
export async function forceTokenRefresh(getLoggedUserFn, refreshTokenFn) {
  return tokenManager.forceRefresh(getLoggedUserFn, refreshTokenFn)
}

// ============================================================================
// UTILITÁRIOS
// ============================================================================

/**
 * Obtém estatísticas do sistema de sync
 */
export async function getSyncStats() {
  const queueStats = await syncQueue.getStats()
  return {
    queue: queueStats,
    tokenManager: tokenManager.getStatus(),
    logs: syncLogger.generateReport(),
  }
}

/**
 * Exporta logs para diagnóstico
 */
export function exportSyncLogs() {
  return syncLogger.exportLogs()
}

/**
 * Limpa dados de sync (usar com cuidado!)
 */
export async function clearSyncData() {
  await syncQueue.clearQueue()
  syncLogger.clearLogs()
  tokenManager.clearToken()
}
